import { useCallback, useEffect, useRef, useState } from 'react'

const DRAFT_PREFIX = 'dsc-clinic:draft:'

interface FormDraft {
    restored: boolean
    dismiss: () => void
    clear: () => void
}

/**
 * Keeps an unsaved form in this browser tab so a half-finished consultation or
 * prescription survives an accidental navigation or reload.
 */
export const useFormDraft = <T>(
    key: string | null,
    values: T,
    initialValues: T,
    restore: (values: T) => void
): FormDraft => {
    const [restored, setRestored] = useState(false)
    const readyRef = useRef(false)
    const restoreRef = useRef(restore)

    restoreRef.current = restore

    useEffect(() => {
        readyRef.current = false
        if (!key) return

        try {
            const raw = window.sessionStorage.getItem(`${DRAFT_PREFIX}${key}`)

            if (raw) {
                restoreRef.current(JSON.parse(raw) as T)
                setRestored(true)
            }
        }
        catch {
            // A broken draft is ignored.
        }

        readyRef.current = true
    }, [key])

    useEffect(() => {
        if (!key || !readyRef.current) return

        try {
            const storageKey = `${DRAFT_PREFIX}${key}`

            if (JSON.stringify(values) === JSON.stringify(initialValues)) window.sessionStorage.removeItem(storageKey)
            else window.sessionStorage.setItem(storageKey, JSON.stringify(values))
        }
        catch {
            // Drafts are a convenience only.
        }
    }, [initialValues, key, values])

    const clear = useCallback(() => {
        if (!key) return

        try {
            window.sessionStorage.removeItem(`${DRAFT_PREFIX}${key}`)
        }
        catch {
            // Nothing to clear.
        }
    }, [key])

    const dismiss = useCallback(() => setRestored(false), [])

    return { restored, dismiss, clear }
}

export const useUnsavedChangesWarning = (dirty: boolean): void => {
    useEffect(() => {
        if (!dirty) return

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [dirty])
}
